import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { MapPin, Home, Loader2, ShoppingBag, CreditCard } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useCart } from '../contexts/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Checkout = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { cart, getCartTotal, getCartCurrency, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('mercadopago');
  const [orderId, setOrderId] = useState(null);
  const [step, setStep] = useState(1); // 1: payment selection, 2: contact info, 3: processing
  
  const cartTotal = getCartTotal();
  const cartCurrency = getCartCurrency();

  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    delivery_method: 'pickup',
    shipping_address: ''
  });

  if (cart.length === 0) {
    return (
      <div className="min-h-screen py-20 px-4">
        <div className="container mx-auto max-w-2xl text-center">
          <ShoppingBag className="h-16 w-16 text-slate-600 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-4">Carrito Vacío</h1>
          <Button onClick={() => navigate('/shop')} className="bg-gradient-to-r from-cyan-600 to-emerald-600">
            Ir a la Tienda
          </Button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.delivery_method === 'shipping' && !formData.shipping_address.trim()) {
      toast({ title: "Error", description: "Ingresa tu dirección de envío", variant: "destructive" });
      return;
    }

    setLoading(true);
    setStep(3);

    try {
      // Create order with payment_method based on selected payment method
      const orderResponse = await fetch(`${API}/orders/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: formData.customer_name,
          customer_email: formData.customer_email,
          customer_phone: formData.customer_phone,
          delivery_method: formData.delivery_method,
          shipping_address: formData.delivery_method === 'shipping' ? formData.shipping_address : null,
          payment_method: paymentMethod, // Pass the selected payment method
          items: cart.map(item => ({
            product_id: item.id,
            name: item.name,
            price: item.price,
            quantity: item.quantity
          }))
        })
      });

      if (!orderResponse.ok) throw new Error('Error creando la orden');

      const order = await orderResponse.json();
      setOrderId(order.id);

      // Process payment based on method
      if (paymentMethod === 'mercadopago') {
        await handleMercadoPagoPayment(order.id);
      } else if (paymentMethod === 'paypal') {
        await handlePayPalPayment(order.id, 'USD');
      } else if (paymentMethod === 'cash') {
        await handleCashPayment(order.id);
      }

    } catch (error) {
      console.error('Error:', error);
      toast({ title: "Error", description: error.message || "Hubo un error procesando tu pedido", variant: "destructive" });
      setLoading(false);
      setStep(2);
    }
  };

  const handleMercadoPagoPayment = async (orderIdParam) => {
    try {
      // Get exchange rate if needed
      const orderData = cart;
      const hasUSD = orderData.some(item => item.currency === 'USD');
      
      let conversionNote = '';
      if (hasUSD) {
        // Fetch current exchange rate
        const rateResponse = await fetch(`${API}/exchange-rate`);
        if (rateResponse.ok) {
          const { usd_to_ars } = await rateResponse.json();
          conversionNote = ` (Incluye conversión USD→ARS al tipo de cambio de $${usd_to_ars})`;
        }
      }

      const paymentResponse = await fetch(`${API}/payments/create-preference`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: orderIdParam || orderId })
      });

      if (!paymentResponse.ok) throw new Error('Error creando preferencia de pago');

      const payment = await paymentResponse.json();
      
      if (conversionNote) {
        toast({
          title: "Conversión aplicada",
          description: `El total en USD fue convertido a ARS${conversionNote}`
        });
      }
      
      clearCart();
      window.location.href = payment.init_point;
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      setLoading(false);
      setStep(2);
    }
  };

  const handlePayPalPayment = async (orderIdParam, currency) => {
    try {
      const paymentResponse = await fetch(`${API}/paypal/create-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: orderIdParam || orderId, currency })
      });

      if (!paymentResponse.ok) throw new Error('Error creando pago de PayPal');

      const payment = await paymentResponse.json();
      clearCart();
      window.location.href = payment.approval_url;
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      setLoading(false);
      setStep(2);
    }
  };

  const handleCashPayment = async (orderIdParam) => {
    try {
      toast({
        title: "¡Pedido confirmado!",
        description: "Podrás pagar al retirar tu pedido en el local"
      });
      
      clearCart();
      navigate(`/payment/result?order_id=${orderIdParam || orderId}&status=pending&payment_method=cash`);
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      setLoading(false);
      setStep(2);
    }
  };

  return (
    <div className="min-h-screen py-20 px-4">
      <div className="container mx-auto max-w-5xl">
        <h1 className="text-5xl font-bold mb-8 bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">
          Finalizar Compra
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {step === 1 && (
              <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <CreditCard className="h-6 w-6" />Selecciona tu Método de Pago
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Paso 1 de 2: Elige cómo quieres pagar
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                    <Card className="bg-slate-900/50 border-slate-700">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <RadioGroupItem value="mercadopago" id="mercadopago" data-testid="payment-method-mercadopago" />
                          <Label htmlFor="mercadopago" className="flex-1 cursor-pointer">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-white font-semibold">MercadoPago</p>
                                <p className="text-slate-400 text-sm">Pagar con MercadoPago (ARS)</p>
                              </div>
                              <svg className="h-8" viewBox="0 0 120 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect width="120" height="40" rx="4" fill="#009EE3"/>
                                <text x="60" y="25" fill="white" fontSize="14" fontWeight="bold" textAnchor="middle">
                                  MercadoPago
                                </text>
                              </svg>
                            </div>
                          </Label>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-slate-900/50 border-slate-700">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <RadioGroupItem value="paypal" id="paypal" data-testid="payment-method-paypal" />
                          <Label htmlFor="paypal" className="flex-1 cursor-pointer">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-white font-semibold">PayPal</p>
                                <p className="text-slate-400 text-sm">Pagar en Dólares (USD)</p>
                              </div>
                              <img src="https://www.paypalobjects.com/webstatic/mktg/Logo/pp-logo-100px.png" alt="PayPal" className="h-8" />
                            </div>
                          </Label>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-slate-900/50 border-slate-700">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <RadioGroupItem value="cash" id="cash" data-testid="payment-method-cash" />
                          <Label htmlFor="cash" className="flex-1 cursor-pointer">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-white font-semibold">💵 Pago en el Local</p>
                                <p className="text-slate-400 text-sm">Pagar al retirar en efectivo o tarjeta</p>
                              </div>
                            </div>
                          </Label>
                        </div>
                      </CardContent>
                    </Card>
                  </RadioGroup>

                  <Button 
                    onClick={() => setStep(2)}
                    data-testid="checkout-continue-btn"
                    className="w-full bg-gradient-to-r from-cyan-600 to-emerald-600 text-lg py-6"
                  >
                    Continuar
                  </Button>
                </CardContent>
              </Card>
            )}

            {step === 2 && (
              <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Datos de Contacto y Entrega</CardTitle>
                  <CardDescription className="text-slate-400">
                    Paso 2 de 2: Ingresa tus datos
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-slate-300">Nombre Completo *</Label>
                        <Input
                          required
                          data-testid="customer-name-input"
                          value={formData.customer_name}
                          onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                          className="bg-slate-900/50 border-slate-700 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">Email *</Label>
                        <Input
                          required
                          type="email"
                          data-testid="customer-email-input"
                          value={formData.customer_email}
                          onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                          className="bg-slate-900/50 border-slate-700 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">Teléfono *</Label>
                        <Input
                          required
                          data-testid="customer-phone-input"
                          value={formData.customer_phone}
                          onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                          className="bg-slate-900/50 border-slate-700 text-white"
                        />
                      </div>
                    </div>

                    <div className="space-y-4">
                      <Label className="text-white text-lg">Método de Entrega *</Label>
                      <RadioGroup 
                        value={formData.delivery_method} 
                        onValueChange={(value) => setFormData({...formData, delivery_method: value})}
                      >
                        <Card className="bg-slate-900/50 border-slate-700">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <RadioGroupItem value="pickup" id="pickup" data-testid="delivery-pickup" />
                              <div className="flex-1">
                                <Label htmlFor="pickup" className="text-white font-semibold cursor-pointer flex items-center gap-2">
                                  <MapPin className="h-5 w-5 text-cyan-400" />Retiro en Local
                                </Label>
                                <p className="text-slate-400 text-sm mt-1">
                                  Mariquita Sanchez de Thompson 498, Río Cuarto
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        <Card className="bg-slate-900/50 border-slate-700">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <RadioGroupItem value="shipping" id="shipping" data-testid="delivery-shipping" />
                              <div className="flex-1">
                                <Label htmlFor="shipping" className="text-white font-semibold cursor-pointer flex items-center gap-2">
                                  <Home className="h-5 w-5 text-emerald-400" />Envío a Domicilio
                                </Label>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </RadioGroup>
                    </div>

                    {formData.delivery_method === 'shipping' && (
                      <div className="space-y-2">
                        <Label className="text-slate-300">Dirección de Envío *</Label>
                        <Textarea
                          required={formData.delivery_method === 'shipping'}
                          data-testid="shipping-address-input"
                          value={formData.shipping_address}
                          onChange={(e) => setFormData({...formData, shipping_address: e.target.value})}
                          className="bg-slate-900/50 border-slate-700 text-white"
                          rows={3}
                        />
                      </div>
                    )}

                    <div className="flex gap-3">
                      <Button 
                        type="button"
                        onClick={() => setStep(1)}
                        variant="outline"
                        className="flex-1 border-slate-700 text-white hover:bg-slate-800"
                      >
                        Volver
                      </Button>
                      <Button 
                        type="submit" 
                        disabled={loading}
                        data-testid="checkout-submit-btn"
                        className="flex-1 bg-gradient-to-r from-cyan-600 to-emerald-600 text-lg py-6"
                      >
                        {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Procesando...</> : 'Confirmar Pedido'}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {step === 3 && (
              <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
                <CardContent className="p-12 text-center">
                  <Loader2 className="h-16 w-16 text-cyan-400 mx-auto mb-4 animate-spin" />
                  <h3 className="text-2xl font-bold text-white mb-2">Procesando tu pedido...</h3>
                  <p className="text-slate-400">Por favor espera un momento</p>
                </CardContent>
              </Card>
            )}
          </div>

          <div>
            <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700 sticky top-24">
              <CardHeader>
                <CardTitle className="text-white">Resumen del Pedido</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {cart.map((item) => {
                    const finalPrice = item.discount > 0 
                      ? Math.round(item.price * (1 - item.discount / 100))
                      : item.price;
                    return (
                      <div key={item.id} className="flex flex-col gap-1 border-b border-slate-700 pb-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-300">{item.name} x{item.quantity}</span>
                          {item.discount > 0 ? (
                            <div className="text-right">
                              <span className="text-slate-500 line-through text-xs block">
                                {item.currency === 'USD'
                                  ? `$${item.price * item.quantity} USD`
                                  : `$${(item.price * item.quantity).toLocaleString()} ARS`
                                }
                              </span>
                              <span className="text-emerald-400 font-semibold">
                                {item.currency === 'USD'
                                  ? `$${finalPrice * item.quantity} USD`
                                  : `$${(finalPrice * item.quantity).toLocaleString()} ARS`
                                }
                              </span>
                            </div>
                          ) : (
                            <span className="text-white font-semibold">
                              {item.currency === 'USD'
                                ? `$${item.price * item.quantity} USD`
                                : `$${(item.price * item.quantity).toLocaleString()} ARS`
                              }
                            </span>
                          )}
                        </div>
                        {item.discount > 0 && (
                          <span className="text-red-400 text-xs self-end">-{item.discount}% OFF</span>
                        )}
                      </div>
                    );
                  })}
                  <div className="border-t border-slate-700 pt-4">
                    <div className="flex justify-between text-lg">
                      <span className="text-white font-bold">Total</span>
                      <span className="text-cyan-400 font-bold text-2xl">
                        {cartCurrency === 'MIXED' 
                          ? `$${cartTotal.toLocaleString()} (Mixto)`
                          : cartCurrency === 'USD'
                            ? `$${cartTotal} USD`
                            : `$${cartTotal.toLocaleString()} ARS`
                        }
                      </span>
                    </div>
                    {paymentMethod === 'paypal' && cartCurrency === 'ARS' && (
                      <div className="mt-2 text-slate-400 text-xs space-y-1">
                        <p>Conversión a USD: ~${(cartTotal / 1000).toFixed(2)}</p>
                        <p>*Tipo de cambio aproximado: 1 USD = 1000 ARS</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
